// Collection Filters & AJAX Handler - Premium Implementation
class CollectionFilters extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.config = {
      updateDelay: 300,
      scrollBehavior: 'smooth',
      focusTrap: true,
      announceUpdates: true,
    };

    // State
    this.state = {
      loading: false,
      currentFilters: {},
      currentSort: '',
      currentView: 'grid',
      currentPage: 1,
    };

    // Cache DOM elements
    this.elements = {
      form: this.querySelector('form'),
      grid: document.querySelector('[data-collection-grid]'),
      toolbar: document.querySelector('[data-collection-toolbar]'),
      resultsCount: document.querySelector('[data-results-count]'),
      activeFilters: document.querySelector('[data-active-filters]'),
      loadMoreButton: document.querySelector('[data-load-more]'),
      drawer: document.querySelector('[data-filter-drawer]'),
      backdrop: document.querySelector('[data-filter-backdrop]'),
      liveRegion: document.querySelector('[data-live-region]'),
    };

    // Bind methods
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleViewChange = this.handleViewChange.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handlePopState = this.handlePopState.bind(this);

    // Initialize
    this.init();
  }

  init() {
    // Event listeners
    this.addEventListener('change', this.handleFilterChange);
    this.elements.form?.addEventListener('submit', (e) => e.preventDefault());

    // Sort dropdown
    const sortSelect = document.querySelector('[data-sort-select]');
    sortSelect?.addEventListener('change', this.handleSortChange);

    // View mode toggle
    const viewButtons = document.querySelectorAll('[data-view-toggle]');
    viewButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleViewChange);
    });

    // Load more / infinite scroll
    if (this.elements.loadMoreButton) {
      this.elements.loadMoreButton.addEventListener('click', this.handleLoadMore);
    }

    // Intersection observer for infinite scroll
    if (this.dataset.infiniteScroll === 'true') {
      this.initInfiniteScroll();
    }

    // Popstate for browser back/forward
    window.addEventListener('popstate', this.handlePopState);

    // Mobile drawer
    this.initMobileDrawer();

    // Parse initial state from URL
    this.parseUrlState();

    // Accessibility
    this.initAccessibility();
  }

  // Filter change handler
  handleFilterChange(event) {
    const input = event.target;
    if (!input.matches('input[type="checkbox"], input[type="radio"], input[type="number"]')) return;

    // Debounce price inputs
    if (input.type === 'number') {
      clearTimeout(this.priceTimeout);
      this.priceTimeout = setTimeout(() => {
        this.updateFilters();
      }, this.config.updateDelay);
    } else {
      this.updateFilters();
    }
  }

  // Sort change handler
  handleSortChange(event) {
    this.state.currentSort = event.target.value;
    this.updateCollection();
  }

  // View mode change handler
  handleViewChange(event) {
    const button = event.currentTarget;
    const view = button.dataset.view;
    const toggle = button.closest('.collection-view-toggle');

    // Update buttons
    document.querySelectorAll('[data-view-toggle]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.view === view);
    });

    // Update toggle indicator
    if (toggle) {
      toggle.dataset.active = view;
    }

    // Update grid
    this.elements.grid?.classList.remove('collection-grid--grid', 'collection-grid--list');
    this.elements.grid?.classList.add(`collection-grid--${view}`);

    this.state.currentView = view;
    this.updateUrl();
  }

  // Update filters and collection
  async updateFilters() {
    // Collect active filters
    const formData = new FormData(this.elements.form);
    const filters = {};

    for (const [key, value] of formData.entries()) {
      if (!filters[key]) filters[key] = [];
      filters[key].push(value);
    }

    this.state.currentFilters = filters;
    this.state.currentPage = 1; // Reset to first page

    await this.updateCollection();
  }

  // Main update function using Sections API
  async updateCollection(append = false) {
    if (this.state.loading) return;

    this.state.loading = true;
    this.showLoading();

    try {
      // Build URL with filters
      const url = this.buildUrl();

      // Add section rendering params
      url.searchParams.set('section_id', 'main-collection-premium');

      // Fetch updated section
      const response = await fetch(url.toString());
      const html = await response.text();

      // Parse response
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(html, 'text/html');

      // Update components
      this.updateGrid(newDocument, append);
      this.updateToolbar(newDocument);
      this.updateActiveFilters(newDocument);
      this.updatePagination(newDocument);

      // Update URL without reload (preserve scroll position for infinite scroll)
      this.updateUrl(append);

      // Announce to screen readers
      this.announceUpdate();

      // Trigger custom event
      this.dispatchEvent(
        new CustomEvent('collection:updated', {
          detail: { filters: this.state.currentFilters, sort: this.state.currentSort },
        })
      );
    } catch (error) {
      console.error('Failed to update collection:', error);
      this.showError();

      this.dispatchEvent(
        new CustomEvent('collection:error', {
          detail: { error },
        })
      );
    } finally {
      this.state.loading = false;
      this.hideLoading();
    }
  }

  // Update grid content
  updateGrid(newDocument, append = false) {
    const newGrid = newDocument.querySelector('[data-collection-grid]');
    const emptyState = newDocument.querySelector('.collection-empty');

    // Check if collection is empty
    if (emptyState) {
      this.elements.grid.innerHTML = emptyState.outerHTML;
      return;
    }

    const newProducts = newGrid?.querySelectorAll('.collection-grid__item');

    if (!newProducts?.length) {
      this.showEmptyState();
      return;
    }

    if (append) {
      // Append for load more / infinite scroll
      newProducts.forEach((product) => {
        this.elements.grid.appendChild(product.cloneNode(true));
      });
    } else {
      // Replace grid content
      this.elements.grid.innerHTML = newGrid.innerHTML;

      // Scroll to top if configured
      if (this.dataset.scrollTop === 'true') {
        this.elements.grid.scrollIntoView({ behavior: this.config.scrollBehavior });
      }
    }

    // Reinitialize product cards if needed
    if (window.ProductCard) {
      this.elements.grid.querySelectorAll('[data-product-card]').forEach((card) => {
        new ProductCard(card);
      });
    }
  }

  // Update toolbar (results count, etc.)
  updateToolbar(newDocument) {
    const newCount = newDocument.querySelector('[data-results-count]');
    if (newCount && this.elements.resultsCount) {
      this.elements.resultsCount.textContent = newCount.textContent;
    }
  }

  // Update active filter chips
  updateActiveFilters(newDocument) {
    const newActiveFilters = newDocument.querySelector('[data-active-filters]');
    if (newActiveFilters && this.elements.activeFilters) {
      this.elements.activeFilters.innerHTML = newActiveFilters.innerHTML;

      // Rebind remove buttons
      this.elements.activeFilters.querySelectorAll('[data-remove-filter]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.removeFilter(btn.dataset.filterKey, btn.dataset.filterValue);
        });
      });
    }
  }

  // Update pagination
  updatePagination(newDocument) {
    const newPagination = newDocument.querySelector('[data-pagination]');
    const currentPagination = document.querySelector('[data-pagination]');

    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    }

    // Update load more button
    const newLoadMore = newDocument.querySelector('[data-load-more]');
    if (this.elements.loadMoreButton && newLoadMore) {
      this.elements.loadMoreButton.dataset.nextUrl = newLoadMore.dataset.nextUrl;
      this.elements.loadMoreButton.disabled = !newLoadMore.dataset.nextUrl;
    }
  }

  // Build URL with current state
  buildUrl() {
    const url = new URL(window.location.href);

    // Clear existing params
    [...url.searchParams.keys()].forEach((key) => {
      if (key !== 'q') url.searchParams.delete(key);
    });

    // Add filters
    Object.entries(this.state.currentFilters).forEach(([key, values]) => {
      values.forEach((value) => {
        url.searchParams.append(key, value);
      });
    });

    // Add sort
    if (this.state.currentSort) {
      url.searchParams.set('sort_by', this.state.currentSort);
    }

    // Add view
    if (this.state.currentView !== 'grid') {
      url.searchParams.set('view', this.state.currentView);
    }

    // Add page
    if (this.state.currentPage > 1) {
      url.searchParams.set('page', this.state.currentPage);
    }

    return url;
  }

  // Update browser URL without reload
  updateUrl(preserveScroll = false) {
    const url = this.buildUrl();
    
    if (preserveScroll) {
      // Save current scroll position for infinite scroll
      const scrollY = window.scrollY || window.pageYOffset;
      window.history.pushState({ path: url.toString(), scrollY: scrollY }, '', url.toString());
      // Immediately restore scroll position to prevent jump
      window.scrollTo(0, scrollY);
    } else {
      window.history.pushState({ path: url.toString() }, '', url.toString());
    }
  }

  // Parse state from URL
  parseUrlState() {
    const params = new URLSearchParams(window.location.search);

    // Parse filters
    const filters = {};
    const skipParams = ['sort_by', 'view', 'page', 'q', 'section_id'];

    for (const [key, value] of params.entries()) {
      if (!skipParams.includes(key)) {
        if (!filters[key]) filters[key] = [];
        filters[key].push(value);
      }
    }

    this.state.currentFilters = filters;
    this.state.currentSort = params.get('sort_by') || '';
    this.state.currentView = params.get('view') || 'grid';
    this.state.currentPage = parseInt(params.get('page') || '1');

    // Apply state to UI
    this.applyStateToUI();
  }

  // Apply current state to UI elements
  applyStateToUI() {
    // Filters
    Object.entries(this.state.currentFilters).forEach(([key, values]) => {
      values.forEach((value) => {
        const input = this.querySelector(`input[name="${key}"][value="${value}"]`);
        if (input) input.checked = true;
      });
    });

    // Sort
    const sortSelect = document.querySelector('[data-sort-select]');
    if (sortSelect) sortSelect.value = this.state.currentSort;

    // View
    document.querySelectorAll('[data-view-toggle]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.view === this.state.currentView);
    });
  }

  // Handle browser back/forward
  handlePopState(event) {
    this.parseUrlState();
    this.updateCollection();
  }

  // Remove individual filter
  removeFilter(key, value) {
    const input = this.querySelector(`input[name="${key}"][value="${value}"]`);
    if (input) {
      input.checked = false;
      this.updateFilters();
    }
  }

  // Clear all filters
  clearAllFilters() {
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    this.querySelectorAll('input[type="number"]').forEach((input) => {
      input.value = '';
    });

    this.updateFilters();
  }

  // Load more products
  async handleLoadMore() {
    const nextUrl = this.elements.loadMoreButton?.dataset.nextUrl;
    if (!nextUrl || this.state.loading) return;

    this.state.currentPage++;
    await this.updateCollection(true);
  }

  // Initialize infinite scroll
  initInfiniteScroll() {
    const sentinel = document.querySelector('[data-infinite-scroll-sentinel]');
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.state.loading) {
            this.handleLoadMore();
          }
        });
      },
      {
        rootMargin: '200px',
      }
    );

    observer.observe(sentinel);
  }

  // Mobile drawer functionality
  initMobileDrawer() {
    const toggleButton = document.querySelector('[data-filter-toggle]');
    const closeButton = this.elements.drawer?.querySelector('[data-drawer-close]');

    toggleButton?.addEventListener('click', () => this.openDrawer());
    closeButton?.addEventListener('click', () => this.closeDrawer());
    this.elements.backdrop?.addEventListener('click', () => this.closeDrawer());

    // Apply button in drawer
    const applyButton = this.elements.drawer?.querySelector('[data-drawer-apply]');
    applyButton?.addEventListener('click', () => {
      this.updateFilters();
      this.closeDrawer();
    });
  }

  openDrawer() {
    this.elements.drawer?.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus trap
    if (this.config.focusTrap) {
      this.trapFocus(this.elements.drawer);
    }

    this.dispatchEvent(new CustomEvent('collection:filters:open'));
  }

  closeDrawer() {
    this.elements.drawer?.classList.remove('is-open');
    document.body.style.overflow = '';

    // Return focus
    const toggleButton = document.querySelector('[data-filter-toggle]');
    toggleButton?.focus();

    this.dispatchEvent(new CustomEvent('collection:filters:close'));
  }

  // Accessibility helpers
  initAccessibility() {
    // Accordion behavior for filter groups
    this.querySelectorAll('[data-filter-header]').forEach((header) => {
      header.addEventListener('click', () => {
        const group = header.closest('[data-filter-group]');
        const isOpen = group.classList.contains('is-open');

        group.classList.toggle('is-open');
        header.setAttribute('aria-expanded', !isOpen);

        const body = group.querySelector('[data-filter-body]');
        body.hidden = isOpen;
      });
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.elements.drawer?.classList.contains('is-open')) {
        this.closeDrawer();
      }
    });
  }

  // Focus trap for drawer
  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    firstFocusable?.focus();

    element.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    });
  }

  // Loading states
  showLoading() {
    this.elements.grid?.classList.add('collection-grid--loading');

    // Show skeleton if configured
    if (this.dataset.skeleton === 'true') {
      this.showSkeleton();
    }
  }

  hideLoading() {
    this.elements.grid?.classList.remove('collection-grid--loading');
    this.hideSkeleton();
  }

  showSkeleton() {
    const skeleton = document.querySelector('[data-collection-skeleton]');
    skeleton?.classList.remove('hidden');
  }

  hideSkeleton() {
    const skeleton = document.querySelector('[data-collection-skeleton]');
    skeleton?.classList.add('hidden');
  }

  // Empty state
  showEmptyState() {
    const emptyState = document.querySelector('[data-empty-state]');
    emptyState?.classList.remove('hidden');
    this.elements.grid?.classList.add('hidden');
  }

  // Error handling
  showError() {
    // Show toast or inline error
    const message = this.dataset.errorMessage || 'Failed to update collection';
    console.error(message);

    // Could integrate with a toast notification system
    if (window.Toast) {
      window.Toast.show(message, 'error');
    }
  }

  // Screen reader announcements
  announceUpdate() {
    if (!this.config.announceUpdates || !this.elements.liveRegion) return;

    const count = this.elements.resultsCount?.textContent || '0 products';
    this.elements.liveRegion.textContent = `Collection updated. Showing ${count}`;
  }
}

// Define custom element
customElements.define('collection-filters', CollectionFilters);

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  // Initialize any collection filter elements on the page
  document.querySelectorAll('collection-filters:not(.initialized)').forEach((element) => {
    element.classList.add('initialized');
  });
});
